import { useCallback, useRef, useState } from "react";
import Konva from "konva";
import { DrawingTool, Stroke, ToolSettings } from "../types/chart";

let strokeCounter = 0;
function nextId(): string {
  strokeCounter += 1;
  return `stroke-${Date.now()}-${strokeCounter}`;
}

interface UseDrawingToolOptions {
  toolSettings: ToolSettings;
  onStrokeComplete: (stroke: Stroke) => void;
  onEraseStroke: (strokeId: string) => void;
  onTextInputRequest?: (pos: { x: number; y: number }) => void;
  onSelectStroke?: (strokeId: string) => void;
  onDeselectStroke?: () => void;
}

/**
 * Resolves image-space pointer position from a Konva stage,
 * accounting for stage scale and offset.
 */
function getImageSpacePos(stage: Konva.Stage): { x: number; y: number } | null {
  const pos = stage.getPointerPosition();
  if (!pos) return null;
  const transform = stage.getAbsoluteTransform().copy().invert();
  return transform.point(pos);
}

function defaultOpacity(tool: DrawingTool): number {
  return tool === "highlighter" ? 0.4 : 1;
}

function defaultWidth(tool: DrawingTool, width: number): number {
  return tool === "highlighter" ? Math.max(width * 3, 12) : width;
}

function defaultColor(tool: DrawingTool, color: string): string {
  return tool === "highlighter" ? "#FFEB3B" : color;
}

export function useDrawingTool({
  toolSettings,
  onStrokeComplete,
  onEraseStroke,
  onTextInputRequest,
  onSelectStroke,
  onDeselectStroke,
}: UseDrawingToolOptions) {
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
  // Shadow the in-flight stroke in a ref so handleMouseMove / handleMouseUp
  // can read + commit it WITHOUT going through a setState updater. React
  // 18 StrictMode double-invokes updater functions to catch impure logic;
  // when `onStrokeComplete` (a side effect that dispatches addStroke to
  // the parent) lived inside `setCurrentStroke((prev) => …)`, both
  // invocations fired it with the same `prev`, causing the same stroke
  // id to be appended twice — the source of the
  // "Encountered two children with the same key" warning Joseph saw on
  // every drag.
  const currentStrokeRef = useRef<Stroke | null>(null);
  const commitCurrentStroke = useCallback((next: Stroke | null) => {
    currentStrokeRef.current = next;
    setCurrentStroke(next);
  }, []);
  const isDrawing = useRef(false);
  const startPos = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;
      const pos = getImageSpacePos(stage);
      if (!pos) return;

      const { tool, color, width } = toolSettings;

      if (tool === "select") {
        const target = e.target as Konva.Shape;
        const strokeId = target.getAttr?.("data-stroke-id") as
          | string
          | undefined;
        if (strokeId) {
          onSelectStroke?.(strokeId);
        } else {
          onDeselectStroke?.();
        }
        return;
      }

      if (tool === "eraser") {
        // Find the topmost annotation shape under pointer
        const target = e.target as Konva.Shape;
        const strokeId = target.getAttr?.("data-stroke-id") as
          | string
          | undefined;
        if (strokeId) {
          onEraseStroke(strokeId);
        }
        return;
      }

      if (tool === "text") {
        // Prevent the browser from focusing the Konva stage container,
        // which would immediately blur the about-to-appear text <input>.
        e.evt?.preventDefault();
        onTextInputRequest?.(pos);
        return;
      }

      if (tool === "emoji") {
        if (!toolSettings.emoji) return;
        onStrokeComplete({
          id: nextId(),
          tool: "emoji",
          points: [],
          color: "#000000",
          width: 0,
          opacity: 1,
          x: pos.x,
          y: pos.y,
          text: toolSettings.emoji,
          fontSize: 32,
        });
        return;
      }

      isDrawing.current = true;
      startPos.current = pos;

      const id = nextId();
      const strokeColor = defaultColor(tool, color);
      const strokeWidth = defaultWidth(tool, width);
      const opacity = defaultOpacity(tool);

      if (tool === "pen" || tool === "highlighter") {
        commitCurrentStroke({
          id,
          tool,
          points: [pos.x, pos.y],
          color: strokeColor,
          width: strokeWidth,
          opacity,
        });
      } else if (tool === "rectangle") {
        commitCurrentStroke({
          id,
          tool,
          points: [],
          color,
          width,
          opacity: 1,
          x: pos.x,
          y: pos.y,
          endX: pos.x,
          endY: pos.y,
        });
      } else if (tool === "circle") {
        commitCurrentStroke({
          id,
          tool,
          points: [],
          color,
          width,
          opacity: 1,
          x: pos.x,
          y: pos.y,
          radius: 0,
        });
      } else if (tool === "arrow") {
        commitCurrentStroke({
          id,
          tool,
          points: [pos.x, pos.y, pos.x, pos.y],
          color,
          width,
          opacity: 1,
        });
      }
    },
    [
      toolSettings,
      onStrokeComplete,
      onEraseStroke,
      onTextInputRequest,
      onSelectStroke,
      onDeselectStroke,
      commitCurrentStroke,
    ],
  );

  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isDrawing.current) return;
      const stage = e.target.getStage();
      if (!stage) return;
      const pos = getImageSpacePos(stage);
      if (!pos) return;

      const prev = currentStrokeRef.current;
      if (!prev) return;

      let next: Stroke = prev;
      if (prev.tool === "pen" || prev.tool === "highlighter") {
        next = { ...prev, points: [...prev.points, pos.x, pos.y] };
      } else if (prev.tool === "rectangle") {
        next = { ...prev, endX: pos.x, endY: pos.y };
      } else if (prev.tool === "circle") {
        const dx = pos.x - (prev.x ?? 0);
        const dy = pos.y - (prev.y ?? 0);
        next = { ...prev, radius: Math.sqrt(dx * dx + dy * dy) };
      } else if (prev.tool === "arrow" && startPos.current) {
        next = {
          ...prev,
          points: [startPos.current.x, startPos.current.y, pos.x, pos.y],
        };
      }

      commitCurrentStroke(next);
    },
    [commitCurrentStroke],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    startPos.current = null;

    // Snapshot via ref + commit null in a non-updater write, so the
    // side-effecting onStrokeComplete call below runs exactly once even
    // under React 18 StrictMode (which double-invokes setState updaters
    // for verification — historically this duplicated the stroke).
    const prev = currentStrokeRef.current;
    commitCurrentStroke(null);
    if (!prev) return;

    const hasContent =
      (prev.tool === "pen" || prev.tool === "highlighter") &&
      prev.points.length >= 4;
    const hasShape =
      prev.tool === "rectangle" &&
      prev.x !== undefined &&
      prev.endX !== undefined &&
      Math.abs(prev.endX - prev.x) > 2;
    const hasCircle = prev.tool === "circle" && (prev.radius ?? 0) > 2;
    const hasArrow = prev.tool === "arrow" && prev.points.length === 4;

    if (hasContent || hasShape || hasCircle || hasArrow) {
      onStrokeComplete(prev);
    }
  }, [onStrokeComplete, commitCurrentStroke]);

  return {
    currentStroke,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
